import React, { useEffect, useState, useMemo } from "react";
import { Image, ImageStyle, Text, View } from "react-native";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppColors } from "../../utility/AppColors";
import { AppImages } from "../../utility/AppImages";
import { CreateResponsiveStyle } from "rn-responsive-styles";
import { useLiveStream } from "../../context/LiveStreamContext";
import { useChat } from "../../context/ChatContext";
import LiveStream from "../live-streaming-common-components/LiveStream";
import AudiencePollAndTopGifter from "./AudiencePollAndTopGifter";
import useOpponentStatus from "../../utility/hooks/useOpponentStatus";
import useStreamDetails from "../../utility/hooks/useStreamDetails";
import { useCountdown } from "../../utility/hooks/useCountdown";
import { STREAM_ROLES } from "../../utility/LiveStreamSocket/LiveStreamConfig";

interface UserData {
  AccessToken?: string;
}

interface Participant {
  userID: string;
  profilePicture: string;
  gamesWon: number;
  gamesLost: number;
}

interface StreamDetails {
  organizerTwitchStream?: {
    participantID: string;
    streamType: "Twitch";
    liveStreamURL: string;
    streamStatus: string;
  };
  challengerTwitchStream?: {
    participantID: string;
    streamType: "Twitch";
    liveStreamURL: string;
    streamStatus: string;
  };
  organizerYoutubeStream?: {
    participantID: string;
    streamType: "YouTube";
    liveStreamId: string;
    liveStreamURL: string;
    streamStatus: string;
  };
  challengerYoutubeStream?: {
    participantID: string;
    streamType: "YouTube";
    liveStreamId: string;
    liveStreamURL: string;
    streamStatus: string;
  };
}

interface EventDetails {
  organiserId?: string;
  challengerId?: string;
  generalChatConversationId?: string;
  eventStartTime?: string;
  stakes?: { type: string; quantity: number }[];
  gameTitle?: string;
  status?: string;
  streamingPlatforms?: {
    organiser: string[];
    challenger: string[];
  };
  streamLinks?: {
    organiser: string | null;
    challenger: string | null;
  };
  streamStatus?: {
    organiser: string;
    challenger: string;
  };
}

interface OrganizerData {
  eventDetails: EventDetails | null;
  participantDetails: Participant[] | null;
  streamDetails?: StreamDetails[];
}

interface MatchPreviewComponentProps {
  userData: UserData | null;
  streamJustStarted?: boolean;
  onStreamStarted?: () => void;
  opponentStatus?: "pending" | "live" | "paused" | "completed";
  opponentRenderKey?: number;
  yourRenderKey?: number;
}

const MatchPreviewComponent = ({
  userData,
  streamJustStarted,
  onStreamStarted,
  opponentStatus,
  opponentRenderKey = 0,
  yourRenderKey = 0,
}: MatchPreviewComponentProps) => {
  const styles = useStyles();
  const { organizerData, userRole } = useLiveStream();
  const { checkInStatuses } = useChat();
  const { eventDetails, participantDetails } = (organizerData ||
    {}) as OrganizerData;

  const { organiser, challenger, yourScreen, opponentScreen } = useMemo(() => {
    const organiser = participantDetails?.find(
      (p) => p.userID === eventDetails?.organiserId
    );
    const challenger = participantDetails?.find(
      (p) => p.userID === eventDetails?.challengerId
    );
    return {
      organiser,
      challenger,
      yourScreen: userRole === STREAM_ROLES.ORGANISER ? organiser : challenger,
      opponentScreen:
        userRole === STREAM_ROLES.ORGANISER ? challenger : organiser,
    };
  }, [participantDetails, eventDetails, userRole]);

  const { opponentId, isOpponentOnline } = useOpponentStatus({
    userData,
    eventDetails,
    checkInStatuses,
    userRole,
  });

  const {
    opponentPlatform,
    yourPlatform,
    opponentStreamId,
    yourStreamId,
    isStreamLive,
    isYourStreamLive,
    isOpponentStreamLive,
  } = useStreamDetails({
    organizerData,
    userRole,
  });

  // Simple state: show your preview after stream starts
  const [showYourPreview, setShowYourPreview] = useState(false);

  // When streamJustStarted becomes true, show your preview immediately
  useEffect(() => {
    if (streamJustStarted) {
      setShowYourPreview(true);
      onStreamStarted?.();
    }
  }, [streamJustStarted, onStreamStarted]);

  // If your stream was already live when page loads (e.g., page reload), show preview immediately
  useEffect(() => {
    if (isYourStreamLive && !streamJustStarted) {
      setShowYourPreview(true);
    }
  }, [isYourStreamLive, streamJustStarted]);

  // Debug opponent stream status
  useEffect(() => {
    console.log("=== OPPONENT STREAM DEBUG ===");
    console.log("isOpponentStreamLive:", isOpponentStreamLive);
    console.log("opponentStreamId:", opponentStreamId);
    console.log("opponentPlatform:", opponentPlatform);
    console.log("organizerData.streamDetails:", organizerData?.streamDetails);
    console.log("eventDetails.streamStatus:", eventDetails?.streamStatus);
    console.log(
      "shouldShowOpponentStream:",
      isOpponentStreamLive && opponentStreamId
    );
  }, [
    isOpponentStreamLive,
    opponentStreamId,
    opponentPlatform,
    organizerData?.streamDetails,
    eventDetails?.streamStatus,
  ]);

  const countdown = useCountdown(eventDetails?.eventStartTime, "hh:mm:ss");

  const timeUnits = useMemo(() => {
    const [hours = "00", minutes = "00", seconds = "00"] = countdown.split(":");
    return { hours, minutes, seconds };
  }, [countdown]);

  const wager = useMemo(() => {
    const stakesValue = eventDetails?.stakes?.find((s) => s.quantity > 0);
    return stakesValue ? `${stakesValue.type}: ${stakesValue.quantity}` : "";
  }, [eventDetails?.stakes]);

  // Determine if we should show opponent's live stream (based on socket or API status)
  const isOpponentLiveFromSocket = opponentStatus === "live";
  const showOpponentPauseOverlay = opponentStatus === "paused";
  const shouldShowOpponentStream =
    (isOpponentLiveFromSocket || isOpponentStreamLive) && opponentStreamId;

  // Determine if we should show your live stream
  const shouldShowYourStream =
    (showYourPreview || isYourStreamLive) && yourStreamId;

  return (
    <>
      <View style={styles.container}>
        {/* Opponent Screen Preview */}
        <View style={styles.mainPreview}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_12, styles.header]}>
            Opponent Screen Preview
          </Text>
          <View style={styles.previewSection}>
            {shouldShowOpponentStream ? (
              <View
                style={{ width: "100%", height: "100%", position: "relative" }}
              >
                <LiveStream
                  key={`opponent-${opponentRenderKey}`}
                  platform={opponentPlatform}
                  streamId={opponentStreamId}
                  width="100%"
                  height="100%"
                  style={styles.liveStream}
                  preventRedirect={true}
                />
                {showOpponentPauseOverlay && (
                  <View
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0.7)",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_16,
                        { color: AppColors.white },
                      ]}
                    >
                      Stream Paused
                    </Text>
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_12,
                        { color: AppColors.gray_9499A4, marginTop: 8 },
                      ]}
                    >
                      Waiting for opponent to reconnect...
                    </Text>
                  </View>
                )}
              </View>
            ) : (
              <View style={styles.characterContainer}>
                <View style={styles.characterImageContainer}>
                  <Image
                    source={{
                      uri:
                        opponentScreen?.profilePicture || AppImages.icProfile,
                    }}
                    style={styles.characterImage as ImageStyle}
                  />
                </View>

                <Text
                  style={[AppFontStyle.SEMIBOLD_ANYBODY_16, styles.username]}
                >
                  {opponentScreen?.userID || opponentId || "zenitsu"}
                </Text>
                <Text
                  style={[AppFontStyle.REGULAR_ANYBODY_14, styles.statsText]}
                >
                  {opponentScreen
                    ? `${opponentScreen.gamesWon}W - ${opponentScreen.gamesLost}L`
                    : "0W - 0L"}
                </Text>
                <View style={styles.statusContainer}>
                  <View
                    style={[
                      styles.onlineDot,
                      {
                        backgroundColor: isOpponentOnline
                          ? AppColors.green_00B536
                          : AppColors.gray_9499A4,
                      },
                    ]}
                  />
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      styles.statusText,
                      {
                        color: isOpponentOnline
                          ? AppColors.green_00B536
                          : AppColors.gray_9499A4,
                        marginTop: 2,
                      },
                    ]}
                  >
                    {isOpponentOnline ? "Online" : "Offline"}
                  </Text>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Your Screen Preview */}
        <View style={styles.mainPreview}>
          <Text style={[AppFontStyle.BOLD_ANYBODY_12, styles.header]}>
            Your Screen - Audience Preview
          </Text>
          <View style={styles.previewSection}>
            {shouldShowYourStream ? (
              <LiveStream
                key={`your-${yourRenderKey}`}
                platform={yourPlatform}
                streamId={yourStreamId}
                width="100%"
                height="100%"
                style={styles.liveStream}
                preventRedirect={true}
              />
            ) : (
              <View style={styles.characterContainer}>
                <View style={styles.characterImageContainer}>
                  <Image
                    source={{
                      uri: yourScreen?.profilePicture || AppImages.icProfile,
                    }}
                    style={styles.characterImage as ImageStyle}
                  />
                </View>
                <Text
                  style={[AppFontStyle.SEMIBOLD_ANYBODY_16, styles.username]}
                >
                  {yourScreen?.userID || eventDetails?.organiserId || "tanjiro"}
                </Text>
                <Text
                  style={[AppFontStyle.REGULAR_ANYBODY_14, styles.statsText]}
                >
                  {yourScreen
                    ? `${yourScreen.gamesWon}W - ${yourScreen.gamesLost}L`
                    : "0W - 0L"}
                </Text>
                <View style={styles.statusContainer}>
                  <View
                    style={[
                      styles.onlineDot,
                      {
                        backgroundColor: AppColors.green_00B536,
                      },
                    ]}
                  />
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      styles.statusText,
                      {
                        color: AppColors.green_00B536,
                        marginTop: 2,
                      },
                    ]}
                  >
                    Online
                  </Text>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Center countdown section - only shown when no streams are live */}
        {!isStreamLive && !isYourStreamLive && !isOpponentStreamLive && (
          <View style={styles.centerSection}>
            <Text style={[AppFontStyle.BOLD_ANYBODY_18, styles.centerHeader]}>
              Match Starting In
            </Text>
            <View style={styles.timerContainer}>
              <View style={styles.timeUnit}>
                <View style={styles.timeBox}>
                  <Text style={[AppFontStyle.BOLD_ANYBODY_36, styles.timeText]}>
                    {timeUnits.hours}
                  </Text>
                </View>
                <Text
                  style={[AppFontStyle.REGULAR_ANYBODY_12, styles.timeLabel]}
                >
                  Hours
                </Text>
              </View>
              <View style={styles.timeUnit}>
                <View style={styles.timeBox}>
                  <Text style={[AppFontStyle.BOLD_ANYBODY_36, styles.timeText]}>
                    {timeUnits.minutes}
                  </Text>
                </View>
                <Text
                  style={[AppFontStyle.REGULAR_ANYBODY_12, styles.timeLabel]}
                >
                  Minutes
                </Text>
              </View>
              <View style={styles.timeUnit}>
                <View style={styles.timeBox}>
                  <Text style={[AppFontStyle.BOLD_ANYBODY_36, styles.timeText]}>
                    {timeUnits.seconds}
                  </Text>
                </View>
                <Text
                  style={[AppFontStyle.REGULAR_ANYBODY_12, styles.timeLabel]}
                >
                  Second
                </Text>
              </View>
            </View>
            <View style={styles.infoBox}>
              <Text style={[AppFontStyle.REGULAR_ANYBODY_12, styles.infoLabel]}>
                Wager Value
              </Text>
              <Text style={[AppFontStyle.BOLD_ANYBODY_16, styles.infoValue]}>
                {wager}
              </Text>
            </View>
            <View style={styles.infoBox}>
              <Text style={[AppFontStyle.REGULAR_ANYBODY_12, styles.infoLabel]}>
                Game
              </Text>
              <Text style={[AppFontStyle.BOLD_ANYBODY_16, styles.infoValue]}>
                {eventDetails?.gameTitle || "Rocket League"}
              </Text>
            </View>
          </View>
        )}
      </View>

      {isStreamLive && (
        <View style={{ marginTop: 12, width: "100%", marginBottom: 20 }}>
          <AudiencePollAndTopGifter />
        </View>
      )}
    </>
  );
};

export default MatchPreviewComponent;

const useStyles = CreateResponsiveStyle({
  container: {
    flexDirection: "row",
    height: 300,
    justifyContent: "space-between",
    backgroundColor: AppColors.grayColor_282828,
    borderRadius: 12,
    padding: 12,
    gap: 12,
    width: "100%",
    position: "relative",
    overflow: "hidden",
  },
  mainPreview: {
    flex: 1,
  },
  previewSection: {
    backgroundColor: AppColors.sidebarBorder_374151,
    borderRadius: 8,
    padding: 12,
    alignItems: "center",
    height: "90%",
    justifyContent: "center",
    flex: 1,
    position: "relative",
  },
  header: {
    color: AppColors.white_C3C3C3,
    marginBottom: 8,
  },
  characterContainer: {
    alignItems: "center",
    gap: 8,
    marginTop: 25,
  },
  characterImage: {
    width: 100,
    height: 150,
    borderWidth: 0,
    transform: [{ translateX: -10 }, { translateY: 10 }],
    transformOrigin: "top left",
    overflow: "visible",
  },
  characterImageContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 2,
    borderColor: AppColors.purple_6D28D9,
    overflow: "hidden",
    backgroundColor: AppColors.lightPurple_604e74,
  },
  username: {
    color: AppColors.white,
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  onlineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusText: {
    color: AppColors.green_00B536,
  },
  centerSection: {
    position: "absolute",
    top: "53%",
    left: "50%",
    transform: [{ translateX: "-50%" }, { translateY: "-50%" }],
    width: "25%",
    backgroundColor: AppColors.black_18151b,
    borderRadius: 12,
    padding: 16,
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    height: "80%",
  },
  centerHeader: {
    color: AppColors.white,
  },
  timerText: {
    color: AppColors.white,
    textAlign: "center",
  },
  timerContainer: {
    flexDirection: "row",
    gap: 12,
    alignItems: "center",
    justifyContent: "space-between",
    marginVertical: 4,
  },
  timeUnit: {
    alignItems: "center",
    gap: 6,
  },
  timeBox: {
    backgroundColor: "#DC262666",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 8,
    minWidth: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  timeText: {
    color: AppColors.white,
    fontSize: 18,
  },
  timeLabel: {
    color: AppColors.white,
    fontSize: 8,
  },
  infoBox: {
    backgroundColor: AppColors.grayColor_282828,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 8,
    width: "100%",
    height: 55,
    textAlign: "left",
    borderWidth: 1,
  },
  infoLabel: {
    color: AppColors.gray_88888C,
  },
  infoValue: {
    color: AppColors.white,
    marginTop: 6,
  },
  statsText: {
    color: AppColors.purple_C4B5FD,
  },
  liveStream: {
    borderRadius: 8,
    overflow: "hidden",
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
  },
  preparingText: {
    color: AppColors.white,
    textAlign: "center",
  },
  preparingContainer: {
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
});
